import { IntersectOnCondition } from '@cast/shared-types';
import {
  AksCommitmentPlanEnum,
  AzureSavingsPlanTermEnum,
  CommitmentAssignment,
  CommitmentClusterAggregatedUsage,
  CommitmentInstanceTypeUsage,
  CommitmentState,
  CommitmentStatus,
  CommitmentTermUnit,
  CommitmentUsageDistribution,
  GkeCommitmentPlanEnum,
} from '@cast/types';

import { SUPPORTS_COMMITMENTS } from 'constants/cluster';

export enum CommitmentCloudService {
  AZURE_RI = 'AZURE_RI',
  AZURE_SP = 'AZURE_SP',
  GCP_RESOURCE_CUD = 'GCP_RESOURCE_CUD',
  GCP_FLEX_CUD = 'GCP_FLEX_CUD',
  AWS_RI = 'AWS_RI',
  AWS_SP = 'AWS_SP',
  AWS_ODCR = 'AWS_ODCR',
  AWS_CAPACITY_BLOCK = 'AWS_CAPACITY_BLOCK',
  GCP_RESERVATION = 'GCP_RESERVATION',
}

export type CommitmentProvider = (typeof SUPPORTS_COMMITMENTS)[number];

export type NormalizedCommitmentBase<CS extends CommitmentCloudService> = {
  id: string;
  name: string;
  expirationDate?: string;
  startDate?: string;
  updatedAt?: string;
  region: string;
  status: CommitmentStatus;
  state?: CommitmentState;
  prioritization: boolean;
  autoAssignment: boolean;
  allowedUsage: number;
  assignments: CommitmentAssignment[];
  cloudService: CS;
  provider: CommitmentProvider;
};

export type NormalizedCommitmentUsage<
  WithInstanceTypes extends boolean = false
> = {
  instanceTypesUsage?: CommitmentInstanceTypesUsage<WithInstanceTypes>;
  utilization: number;
  utilizationByCluster: Record<string, number>;
};

export type NormalizedResourceCommitmentUsage = {
  reservedCpu: number;
  reservedMem: number;
  effectiveReservedCpu?: number;
  effectiveReservedMem?: number;
  provisionedCpu: number;
  provisionedMem: number;
};

export type NormalizedSpendCommitmentUsage = {
  reservedPrice: number;
  effectiveReservedPrice?: number;
  provisionedPrice: number;
  provisionedPriceByOthers: number;
};

type HasInstanceTypes = {
  instanceTypes?: Array<CommitmentInstanceTypeUsage>;
};

type CommitmentInstanceTypesUsage<WithInstanceTypes extends boolean = false> = {
  usage?: number | null;
  usageDistribution?: CommitmentUsageDistribution;
  clusters?: Array<CommitmentClusterAggregatedUsage>;
} & IntersectOnCondition<
  HasInstanceTypes,
  WithInstanceTypes,
  '__withInstanceTypes'
>;

export type NormalizedAzureRiCommitment<
  WithInstanceTypes extends boolean = false
> = NormalizedCommitmentBase<CommitmentCloudService.AZURE_RI> &
  NormalizedCommitmentUsage<WithInstanceTypes> &
  NormalizedResourceCommitmentUsage & {
    plan?: AksCommitmentPlanEnum;
    product: string;
    reservedCount: number;
  };

export type NormalizedAzureSpCommitment<
  WithInstanceTypes extends boolean = false
> = NormalizedCommitmentBase<CommitmentCloudService.AZURE_SP> &
  NormalizedCommitmentUsage<WithInstanceTypes> &
  NormalizedSpendCommitmentUsage & {
    product: string;
    planTerm?: AzureSavingsPlanTermEnum;
  };

export type NormalizedGcpResourceCudCommitment<
  WithInstanceTypes extends boolean = false
> = NormalizedCommitmentBase<CommitmentCloudService.GCP_RESOURCE_CUD> &
  NormalizedCommitmentUsage<WithInstanceTypes> &
  NormalizedResourceCommitmentUsage & {
    plan?: GkeCommitmentPlanEnum;
    product: string;
  };

export type NormalizedGcpFlexCudCommitment<
  WithInstanceTypes extends boolean = false
> = NormalizedCommitmentBase<CommitmentCloudService.GCP_FLEX_CUD> &
  NormalizedCommitmentUsage<WithInstanceTypes> &
  NormalizedSpendCommitmentUsage & {
    product: string;
  };

export type NormalizedAwsOdcrCommitment<
  WithInstanceTypes extends boolean = false
> = NormalizedCommitmentBase<CommitmentCloudService.AWS_ODCR> &
  NormalizedCommitmentUsage<WithInstanceTypes> &
  NormalizedResourceCommitmentUsage & {
    product: string;
    quantity?: number;
    isTargeted: boolean;
    awsCommitmentId: string;
  };

export type NormalizedAwsCapacityBlockCommitment<
  WithInstanceTypes extends boolean = false
> = NormalizedCommitmentBase<CommitmentCloudService.AWS_CAPACITY_BLOCK> &
  NormalizedCommitmentUsage<WithInstanceTypes> &
  NormalizedResourceCommitmentUsage & {
    product: string;
    quantity?: number;
    awsCommitmentId: string;
  };

export type NormalizedGcpReservationCommitment<
  WithInstanceTypes extends boolean = false
> = NormalizedCommitmentBase<CommitmentCloudService.GCP_RESERVATION> &
  NormalizedCommitmentUsage<WithInstanceTypes> &
  NormalizedResourceCommitmentUsage & {
    product: string;
    quantity?: number;
    isSpecific: boolean;
    gcpCommitmentId: string;
  };

export type SpendBasedNormalizedCommitment<
  WithInstanceTypes extends boolean = false
> =
  | NormalizedAwsSpCommitment<WithInstanceTypes>
  | NormalizedAzureSpCommitment<WithInstanceTypes>
  | NormalizedGcpFlexCudCommitment<WithInstanceTypes>;

export type CapacityReservationNormalizedCommitment<
  WithInstanceTypes extends boolean = false
> =
  | NormalizedAwsOdcrCommitment<WithInstanceTypes>
  | NormalizedAwsCapacityBlockCommitment<WithInstanceTypes>
  | NormalizedGcpReservationCommitment<WithInstanceTypes>;

export type CapacityReservationCommitmentCloudService =
  CapacityReservationNormalizedCommitment['cloudService'];

export type SpendBasedCommitmentCloudService =
  SpendBasedNormalizedCommitment['cloudService'];

export type ResourceBasedNormalizedCommitment<
  WithInstanceTypes extends boolean = false
> =
  | NormalizedAwsRiCommitment<WithInstanceTypes>
  | CapacityReservationNormalizedCommitment<WithInstanceTypes>
  | NormalizedGcpResourceCudCommitment<WithInstanceTypes>
  | NormalizedAzureRiCommitment<WithInstanceTypes>;

export type ResourceBasedCommitmentCloudService =
  ResourceBasedNormalizedCommitment['cloudService'];

export type NormalizedAwsRiCommitment<
  WithInstanceTypes extends boolean = false
> = NormalizedCommitmentBase<CommitmentCloudService.AWS_RI> &
  NormalizedCommitmentUsage<WithInstanceTypes> &
  NormalizedResourceCommitmentUsage & {
    product: string;
    reservedCount: number;
    availabilityZone: string;
  };

export type NormalizedAwsSpCommitment<
  WithInstanceTypes extends boolean = false
> = NormalizedCommitmentBase<CommitmentCloudService.AWS_SP> &
  NormalizedCommitmentUsage<WithInstanceTypes> &
  NormalizedSpendCommitmentUsage & {
    product: string;
    planTerm?: CommitmentTermUnit;
    instanceFamily?: string;
  };

export type NormalizedCommitment<WithInstanceTypes extends boolean = false> =
  | NormalizedGcpResourceCudCommitment<WithInstanceTypes>
  | NormalizedAzureRiCommitment<WithInstanceTypes>
  | NormalizedAzureSpCommitment<WithInstanceTypes>
  | NormalizedAwsRiCommitment<WithInstanceTypes>
  | NormalizedAwsSpCommitment<WithInstanceTypes>
  | NormalizedGcpFlexCudCommitment<WithInstanceTypes>
  | NormalizedAwsOdcrCommitment<WithInstanceTypes>
  | NormalizedAwsCapacityBlockCommitment<WithInstanceTypes>
  | NormalizedGcpReservationCommitment<WithInstanceTypes>;
